import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import ProgramFilter from '../../../../components/filters/ProgramFilter';
import {
  useDeleteApiProgramsByIdSiteSettingsDomainsAndDomainMutation,
  useGetApiProgramsByIdSiteSettingsQuery,
  usePostApiProgramsByIdSiteSettingsDomainsMutation,
  usePutApiProgramsByIdSiteSettingsAdminEmailMutation,
} from '../../../../app/athenaApiGen';

// Basic domain validation — allows foo.com, sub.foo.gov, etc.
const DOMAIN_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

// Reusable settings card. Replaces the previous colored side-stripe layout
// with a clean outlined Paper that matches the rest of the admin panels.
// `height: '100%'` + column flex lets two side-by-side cards in a Grid
// stretch to equal height regardless of their content.
const SettingCard = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: 3,
      borderRadius: 1,
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Typography variant="subtitle1" fontWeight={600}>
      {title}
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mt: 0.5, mb: 2 }}
    >
      {description}
    </Typography>
    {children}
  </Paper>
);

const SitewideSettings = () => {
  // Site settings are scoped per program on the backend (Athena v2 Services 0.15.1):
  //   GET    /api/programs/{id}/site-settings
  //   PUT    /api/programs/{id}/site-settings/admin-email
  //   POST   /api/programs/{id}/site-settings/domains
  //   DELETE /api/programs/{id}/site-settings/domains/{domain}
  // The previous unscoped /api/site-settings routes were removed; this panel
  // picks a program first and then queries/mutates against the program-scoped
  // codegen hooks. A platform admin (adminSidewideAccess) sees this section
  // because isAdminGateOpen treats sitewide-access as a superuser override.
  const [programId, setProgramId] = useState<string>('');

  const { data: siteSettings, isLoading } = useGetApiProgramsByIdSiteSettingsQuery(
    { id: programId },
    { skip: !programId },
  );
  const [updateAdminEmail, { isLoading: isUpdatingEmail }] =
    usePutApiProgramsByIdSiteSettingsAdminEmailMutation();
  const [addDomain, { isLoading: isAddingDomain }] =
    usePostApiProgramsByIdSiteSettingsDomainsMutation();
  const [deleteDomain] =
    useDeleteApiProgramsByIdSiteSettingsDomainsAndDomainMutation();

  // --- Local state ---
  const [adminEmailInput, setAdminEmailInput] = useState('');
  const [domainInput, setDomainInput] = useState('');
  const [domainError, setDomainError] = useState('');
  const [emailError, setEmailError] = useState('');

  // Notification state
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationSeverity, setNotificationSeverity] = useState<
    'success' | 'error'
  >('success');

  // Seed the email input once data loads
  useEffect(() => {
    if (siteSettings?.adminEmail) {
      setAdminEmailInput(siteSettings.adminEmail);
    }
  }, [siteSettings?.adminEmail]);

  // --- Notification helpers ---
  const showNotification = (
    message: string,
    severity: 'success' | 'error' = 'success',
  ) => {
    setNotificationMessage(message);
    setNotificationSeverity(severity);
    setNotificationOpen(true);
  };

  const handleNotificationClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') return;
    setNotificationOpen(false);
  };

  // --- Admin email handlers ---
  const handleUpdateEmail = async () => {
    const trimmed = adminEmailInput.trim();
    if (!trimmed) {
      setEmailError('Email address is required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailError('');
    try {
      await updateAdminEmail({
        id: programId,
        handlersUpdateAdminEmailRequest: { adminEmail: trimmed },
      }).unwrap();
      showNotification('Admin email updated successfully.');
    } catch {
      showNotification('Failed to update admin email.', 'error');
    }
  };

  const handleRemoveEmail = async () => {
    try {
      await updateAdminEmail({
        id: programId,
        handlersUpdateAdminEmailRequest: { adminEmail: '' },
      }).unwrap();
      setAdminEmailInput('');
      showNotification('Admin email removed.');
    } catch {
      showNotification('Failed to remove admin email.', 'error');
    }
  };

  // --- Domain handlers ---
  const handleAddDomain = async () => {
    const trimmed = domainInput.trim();
    if (!trimmed) {
      setDomainError('Domain name is required.');
      return;
    }
    if (!DOMAIN_REGEX.test(trimmed)) {
      setDomainError('Please enter a valid domain (e.g. example.gov).');
      return;
    }
    if (siteSettings?.domains?.includes(trimmed)) {
      setDomainError('This domain is already in the approved list.');
      return;
    }
    setDomainError('');
    try {
      await addDomain({
        id: programId,
        handlersAddDomainRequest: { domain: trimmed },
      }).unwrap();
      setDomainInput('');
      showNotification(`Domain "${trimmed}" added.`);
    } catch {
      showNotification('Failed to add domain.', 'error');
    }
  };

  const handleRemoveDomain = async (domain: string) => {
    try {
      await deleteDomain({ id: programId, domain }).unwrap();
      showNotification(`Domain "${domain}" removed.`);
    } catch {
      showNotification('Failed to remove domain.', 'error');
    }
  };

  const handleDomainKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddDomain();
    }
  };

  const currentDomains = siteSettings?.domains ?? [];
  const currentEmail = siteSettings?.adminEmail ?? '';

  // Brand-blue primary action shared by both Update / Add buttons.
  // minWidth ensures Update and Add Domain render at the same width so the
  // two card rows align visually side-by-side.
  const primaryActionSx = {
    borderRadius: 2,
    px: 2.5,
    py: 0.75,
    minWidth: 120,
    backgroundColor: '#1F4D83',
    '&:hover': { backgroundColor: '#163C66' },
    color: '#FFFFFF',
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: 'none',
    '&.Mui-disabled': { opacity: 0.6, color: '#FFFFFF' },
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Box sx={{ mb: 2, maxWidth: 480 }}>
        <ProgramFilter
          programId={programId}
          handleChange={(id) => setProgramId(id ?? '')}
          required
        />
      </Box>

      {!programId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select a program to view its admin email and approved domains.
        </Alert>
      )}

      {programId && isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {programId && !isLoading && (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
      {/* Admin Email Address */}
      <SettingCard
        title="Admin Email Address"
        description="Contact email used for administration issues."
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ gap: 1 }}
        >
          {currentEmail && (
            <Chip
              label={currentEmail}
              onDelete={handleRemoveEmail}
              sx={{
                backgroundColor: '#1F4D83',
                color: '#FFFFFF',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': {
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': { color: '#FFFFFF' },
                },
              }}
            />
          )}
          <TextField
            size="small"
            variant="outlined"
            placeholder="Enter admin email"
            value={adminEmailInput}
            onChange={(e) => {
              setAdminEmailInput(e.target.value);
              if (emailError) setEmailError('');
            }}
            error={!!emailError}
            helperText={emailError}
            sx={{ flex: 1, minWidth: 0 }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={isUpdatingEmail}
            onClick={handleUpdateEmail}
            sx={primaryActionSx}
          >
            {isUpdatingEmail ? 'Saving…' : 'Update'}
          </Button>
        </Stack>
      </SettingCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
      {/* Approved Domains */}
      <SettingCard
        title="Approved Domains"
        description="Users with email addresses from these domains can be added to the system."
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ mb: currentDomains.length ? 2 : 0, gap: 1 }}
        >
          <TextField
            size="small"
            variant="outlined"
            placeholder="example.gov"
            value={domainInput}
            onChange={(e) => {
              setDomainInput(e.target.value);
              if (domainError) setDomainError('');
            }}
            onKeyDown={handleDomainKeyDown}
            error={!!domainError}
            helperText={domainError}
            sx={{ flex: 1, minWidth: 0 }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={isAddingDomain}
            onClick={handleAddDomain}
            sx={primaryActionSx}
          >
            {isAddingDomain ? 'Adding…' : 'Add Domain'}
          </Button>
        </Stack>
        {currentDomains.length > 0 ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {currentDomains.map((domain) => (
              <Chip
                key={domain}
                label={domain}
                onDelete={() => handleRemoveDomain(domain)}
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Stack>
        ) : (
          <Typography
            variant="body2"
            sx={{ mt: 2, color: '#B22222', fontWeight: 500 }}
          >
            No approved domains configured.
          </Typography>
        )}
      </SettingCard>
        </Grid>
      </Grid>
      )}

      {/* Notification Snackbar */}
      <Snackbar
        open={notificationOpen}
        autoHideDuration={4000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notificationSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notificationMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SitewideSettings;
